import React from 'react';
import ProductPurchaseControl from './ProductPurchaseControl';

// GOV13 Step 5 close — presentation-only base-plan change surface (Billing owns base plans). It renders STRICTLY
// from the server plan-change-options projection (each option = resolve_purchase_mode(BASE_PLAN)); it never infers
// upgrade/downgrade, price, or eligibility from a plan name / missing price / currentTier / local usage. The
// current plan shows "Current plan" with no mutation control; a standard target shows "Change to plan"; Enterprise/
// on-request shows "Request review"; a blocked downgrade shows exact structured blockers and NO Checkout action.
export default function BillingPlanChange({ options = [], canPurchase = false, currentPlan = {}, busyKey = null,
                                           onChangePlan, onRequestReview }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white/70 p-4" data-testid="base-plan-change-card">
      <h3 className="text-sm font-semibold text-gray-900 mb-1">Change plan</h3>
      <p className="text-xs text-gray-500 mb-3" data-testid="current-plan-summary">
        Current plan: <span className="font-medium">{currentPlan.display_name || currentPlan.current_plan_key || '—'}</span>
        {currentPlan.version ? ` · ${currentPlan.version}` : ''}
        {currentPlan.grandfathered ? ' · Grandfathered' : ''}
      </p>
      {options.length === 0 ? (
        <p className="text-sm text-gray-500">No plan changes are available right now.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {options.map((env) => (
            <div key={env.product_key} className="border rounded p-3" data-testid={`plan-option-${env.product_key}`}>
              <div className="flex items-center justify-between">
                <p className="font-medium text-gray-900">{env.display_name || env.product_key}</p>
                {env.change_type && !env.already_active && (
                  <span className="text-xs text-gray-400" data-testid={`plan-${env.product_key}-changetype`}>{env.change_type}</span>
                )}
              </div>
              {env.description && (
                <p className="text-xs text-gray-500 mt-1" data-testid={`plan-${env.product_key}-description`}>{env.description}</p>
              )}
              {env.price_guidance && (
                <p className="text-xs text-gray-600 mt-1 font-medium" data-testid={`plan-${env.product_key}-price-guidance`}>{env.price_guidance}</p>
              )}
              {env.tax_note && (
                <p className="text-[11px] text-gray-500 mt-0.5" data-testid={`plan-${env.product_key}-tax-note`}>{env.tax_note}</p>
              )}
              {env.already_active ? (
                <span className="inline-block mt-2 px-2 py-0.5 rounded text-xs bg-purple-100 text-purple-800"
                  data-testid={`plan-${env.product_key}-current`}>Current plan</span>
              ) : (
                <>
                  <ProductPurchaseControl env={env} canPurchase={canPurchase} busyKey={busyKey}
                    actionLabel="Change to plan" onPurchase={onChangePlan} onRequestReview={onRequestReview} />
                  {Array.isArray(env.downgrade_blockers) && env.downgrade_blockers.length > 0 && (
                    <ul className="mt-1 text-xs text-red-600 list-disc pl-4" data-testid={`plan-${env.product_key}-blockers`}>
                      {env.downgrade_blockers.map((b, i) => (
                        <li key={i}>{b.display_name}: {b.source_status === 'USAGE_AUTHORITY_UNAVAILABLE'
                          ? 'usage cannot be verified yet — change blocked for safety'
                          : `in use ${b.current_used} · target allows ${b.target_allowance}`}</li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
